/**
 * Vault State Module
 * Centralized state management with getters/setters
 */

import { on, Events } from "../../../../engine/core/events.js";
import { getPref, setPref } from "../../../../engine/storage/index.js";

// ============================================
// Constants
// ============================================

export const CATEGORY_ALL = "__all__";
export const CATEGORY_RECENT = "__recent__";
export const CATEGORY_SHARED = "__shared__";
export const SIZES_KEY = "orbital-vault-sizes";

// ============================================
// State Variables
// ============================================

let _isOpen = false;
let _selectedFolderId = null;
let _expandedFolders = new Set();
let _draggedDocId = null;
let _draggedShareId = null;
let _searchQuery = "";
let _searchScope = "all"; // "all" or "folder"
let _sortBy = "name"; // "name" or "date"
let _sortOrder = "asc"; // "asc" or "desc"
let _openingDocId = null;
let _ownedSharesCache = [];
let _ownedSharesFetched = false;
let _listenersAttached = false;
let _abortController = null;

// Dependency injection - callbacks from main app
let _callbacks = {
  onDocOpen: null,
  onDocCreate: null,
  onDocDelete: null,
  onShareOpen: null,
  onClose: null,
};

// Storage backend (injected)
let _vaultStorage = null;

// ============================================
// Open State
// ============================================

export function isOpen() {
  return _isOpen;
}

export function setOpen(value) {
  _isOpen = value;
}

// ============================================
// Selected Folder
// ============================================

export function getSelectedFolderId() {
  return _selectedFolderId;
}

export function setSelectedFolderId(id) {
  _selectedFolderId = id;
}

// ============================================
// Expanded Folders
// ============================================

export function getExpandedFolders() {
  return _expandedFolders;
}

export function toggleExpandedFolder(id) {
  if (_expandedFolders.has(id)) {
    _expandedFolders.delete(id);
  } else {
    _expandedFolders.add(id);
  }
}

export function addExpandedFolder(id) {
  _expandedFolders.add(id);
}

// ============================================
// Dragged Doc/Share
// ============================================

export function getDraggedDocId() {
  return _draggedDocId;
}

export function setDraggedDocId(id) {
  _draggedDocId = id;
}

export function getDraggedShareId() {
  return _draggedShareId;
}

export function setDraggedShareId(id) {
  _draggedShareId = id;
}

// ============================================
// Search
// ============================================

export function getSearchQuery() {
  return _searchQuery;
}

export function setSearchQuery(query) {
  _searchQuery = query;
}

export function getSearchScope() {
  return _searchScope;
}

export function setSearchScope(scope) {
  _searchScope = scope;
}

// ============================================
// Sort
// ============================================

export function getSortBy() {
  return _sortBy;
}

export function setSortBy(sortBy) {
  _sortBy = sortBy;
}

export function getSortOrder() {
  return _sortOrder;
}

export function setSortOrder(order) {
  _sortOrder = order;
}

// ============================================
// Opening Doc Guard
// ============================================

export function getOpeningDocId() {
  return _openingDocId;
}

export function setOpeningDocId(id) {
  _openingDocId = id;
}

// ============================================
// Owned Shares Cache
// ============================================

export function getOwnedSharesCache() {
  return [..._ownedSharesCache];
}

// The engine's change to the maps this person shares (engine/sync/share-lifecycle.js).
on(Events.OWNED_SHARES_CHANGED, ({ owned }) => setOwnedSharesCache(owned));

export function setOwnedSharesCache(shares) {
  _ownedSharesCache = shares;
}

export function isOwnedSharesFetched() {
  return _ownedSharesFetched;
}

export function setOwnedSharesFetched(value) {
  _ownedSharesFetched = value;
}

// ============================================
// Listeners State
// ============================================

export function getListenersAttached() {
  return _listenersAttached;
}

export function setListenersAttached(value) {
  _listenersAttached = value;
}

export function getAbortController() {
  return _abortController;
}

export function setAbortController(controller) {
  _abortController = controller;
}

// ============================================
// Callbacks
// ============================================

export function getCallbacks() {
  return _callbacks;
}

export function setCallbacks(cbs) {
  // Reset all callbacks first, then set the ones provided
  _callbacks.onDocOpen = cbs.onDocOpen || null;
  _callbacks.onDocCreate = cbs.onDocCreate || null;
  _callbacks.onDocDelete = cbs.onDocDelete || null;
  _callbacks.onShareOpen = cbs.onShareOpen || null;
  _callbacks.onClose = cbs.onClose || null;
}

// ============================================
// Storage
// ============================================

export function getVaultStorage() {
  return _vaultStorage;
}

export function setVaultStorage(storage) {
  _vaultStorage = storage;
}

// ============================================
// Size Persistence
// ============================================

export function loadSizes() {
  const defaults = {
    panelWidth: 1060,
    foldersWidth: 220,
    recentWidth: 240,
  };

  try {
    const stored = getPref(SIZES_KEY);
    if (!stored) return defaults;
    const parsed = JSON.parse(stored);
    return {
      panelWidth: Number.isFinite(parsed.panelWidth) ? Math.max(500, parsed.panelWidth) : defaults.panelWidth,
      foldersWidth: Number.isFinite(parsed.foldersWidth) ? Math.min(320, Math.max(180, parsed.foldersWidth)) : defaults.foldersWidth,
      recentWidth: typeof parsed.recentWidth === "number" ? parsed.recentWidth : defaults.recentWidth,
    };
  } catch {
    return defaults;
  }
}

export function saveSizes() {
  const panel = document.getElementById("vault-panel");
  const folders = document.getElementById("vault-col-folders");
  const recent = document.getElementById("vault-col-recent");

  const sizes = {
    panelWidth: panel?.getBoundingClientRect().width || 1060,
    foldersWidth: folders?.getBoundingClientRect().width || 220,
    recentWidth: recent ? parseInt(recent.style.width) || 240 : 240,
  };

  setPref(SIZES_KEY, JSON.stringify(sizes));
}

// ============================================
// State Reset (for cleanup)
// ============================================

export function resetState() {
  _isOpen = false;
  _selectedFolderId = null;
  _expandedFolders = new Set();
  _draggedDocId = null;
  _draggedShareId = null;
  _searchQuery = "";
  _searchScope = "all";
  _sortBy = "name";
  _sortOrder = "asc";
  _openingDocId = null;
  _ownedSharesCache = [];
  _ownedSharesFetched = false;
  _listenersAttached = false;
  if (_abortController) {
    _abortController.abort();
    _abortController = null;
  }
}
